import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import * as maplibregl from 'maplibre-gl'
import { setWorkerUrl } from 'maplibre-gl'
import type {
  GeoJSONSource,
  Map as MapLibreMap,
  MapGeoJSONFeature,
  MapLayerMouseEvent,
  MapMouseEvent,
} from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
// Vite-specific worker setup (MapLibre v6 docs: the worker is a separate ES
// module that Vite can't resolve on its own under dev/HMR -- without this,
// vector-tile sources fail with "Worker failed to load" and the map renders
// with no tiles at all, confirmed live). `?worker&url` bundles the worker
// (and its maplibre-gl-shared.mjs sibling) into one self-contained URL;
// plain `?url` is documented as insufficient. Must run once, before any
// `Map` is constructed -- doing it at module scope guarantees that.
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import type { Feature, FeatureCollection, LineString } from 'geojson'
import { Fullscreen, Maximize2, Minimize, Pencil } from 'lucide-react'

import { detectGpsOutliers, formatTimestamp } from '@fit-editor/fit-core'
import type { Activity } from '@fit-editor/fit-core'

import { useThrottled } from '@/hooks/use-throttled'
import { useUnits } from '@/hooks/use-units'
import { formatSpeedMetersPerSecond } from '@/lib/format'
import {
  computeLngLatBounds,
  extractGpsPoints,
  gpsPointsInRange,
  nearestGpsPointByTime,
} from '@/lib/geo'
import type { GpsPoint } from '@/lib/geo'
import {
  baseStyleKey,
  buildBaseStyle,
  buildMapExtras,
  DEFAULT_MAP_LAYER_PREFERENCES,
  getMapApiKeys,
  resolveMapLayers,
  TERRAIN_PITCH,
} from '@/lib/map-layers'
import type { MapExtra, MapLayerEntry, MapLayerPreferences } from '@/lib/map-layers'
import { getThemeColors } from '@/lib/theme-colors'
import { cn } from '@/lib/utils'
import { useActivityStore } from '@/store/activity-store'
import type { Theme } from '@/store/activity-store'

import { getRememberedCamera, rememberCamera } from './camera-memory'
import { MapEditToolbar } from './map-edit-toolbar'
import { MapLayerControl } from './map-layer-control'
import { reconcileMapExtras } from './map-layer-sync'
import { useMapPointEditing } from './use-map-point-editing'

setWorkerUrl(maplibreWorkerUrl)

/** Same look as the layer-menu trigger (`MapLayerControl`), for the stacked on-map buttons. */
const MAP_BUTTON_CLASSNAME =
  'border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring flex h-[29px] w-[29px] items-center justify-center rounded-md border shadow-sm outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50'

/**
 * How the map is sized inside its parent:
 * - `fixed`: a fixed height (the default dashboard layout, page scrolls),
 * - `grow`: fills the parent's remaining height, but never below the fixed
 *   minimum (dashboard data collapsed),
 * - `fill`: fills the parent's height exactly (map maximised).
 * Only CSS changes between them -- the MapLibre instance and its camera
 * are kept.
 */
export type ActivityMapLayout = 'fixed' | 'grow' | 'fill'

export interface ActivityMapProps {
  /** Identifies the currently-loaded activity -- used to decide when to fully recreate the map. */
  activityId: string
  activity: Activity
  layout?: ActivityMapLayout
}

const SECTION_LAYOUT_CLASSNAME: Record<ActivityMapLayout, string> = {
  fixed: '',
  grow: 'min-h-64 flex-1',
  fill: 'min-h-0 flex-1',
}

const CANVAS_LAYOUT_CLASSNAME: Record<ActivityMapLayout, string> = {
  fixed: 'h-[55vh] min-h-72',
  grow: 'min-h-0 flex-1',
  fill: 'min-h-0 flex-1',
}

type AttributionControlRef = {
  current: {
    control: InstanceType<typeof maplibregl.AttributionControl>
    custom: string | undefined
  } | null
}

function emptyFeatureCollection(): FeatureCollection {
  return { type: 'FeatureCollection', features: [] }
}

function pointsToLineString(points: GpsPoint[]): Feature<LineString> {
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'LineString', coordinates: points.map((p) => [p.lon, p.lat]) },
  }
}

function pointsToFeatureCollection(points: GpsPoint[]): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: points.map((p) => ({
      type: 'Feature',
      properties: { recordIndex: p.recordIndex, timestampMs: p.timestampMs },
      geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
    })),
  }
}

function createMarkerElement(kind: 'start' | 'finish', theme: Theme): HTMLDivElement {
  const el = document.createElement('div')
  const bg = kind === 'start' ? '#16a34a' : '#dc2626'
  const label = kind === 'start' ? 'S' : 'F'
  el.style.width = '20px'
  el.style.height = '20px'
  el.style.borderRadius = '9999px'
  el.style.background = bg
  el.style.border = `2px solid ${theme === 'dark' ? '#0a0a0a' : '#ffffff'}`
  el.style.boxShadow = '0 1px 3px rgba(0,0,0,0.5)'
  el.style.display = 'flex'
  el.style.alignItems = 'center'
  el.style.justifyContent = 'center'
  el.style.color = '#ffffff'
  el.style.fontSize = '11px'
  el.style.fontWeight = '700'
  el.style.fontFamily = 'inherit'
  el.textContent = label
  el.setAttribute('aria-hidden', 'true')
  return el
}

function updateSyncSources(
  map: MapLibreMap,
  gpsPoints: GpsPoint[],
  hoveredTimestampMs: number | null,
  selectedRangeMs: { start: number; end: number } | null,
): void {
  const hoverSource = map.getSource(SOURCES.hover) as GeoJSONSource | undefined
  if (hoverSource != null) {
    const hovered =
      hoveredTimestampMs == null ? undefined : nearestGpsPointByTime(gpsPoints, hoveredTimestampMs)
    hoverSource.setData(
      hovered == null ? emptyFeatureCollection() : pointsToFeatureCollection([hovered]),
    )
  }

  const selectedSource = map.getSource(SOURCES.selected) as GeoJSONSource | undefined
  if (selectedSource != null) {
    const inRange =
      selectedRangeMs == null
        ? []
        : gpsPointsInRange(gpsPoints, selectedRangeMs.start, selectedRangeMs.end)
    selectedSource.setData(
      inRange.length >= 2 ? pointsToLineString(inRange) : emptyFeatureCollection(),
    )
  }
}

const SOURCES = {
  route: 'activity-route',
  points: 'activity-points',
  outliers: 'activity-outliers',
  hover: 'activity-hover',
  selected: 'activity-selected-segment',
} as const

/**
 * Route map. Renders nothing map-like (and mounts no MapLibre instance at
 * all) when the activity has no GPS data -- the no-GPS state is handled by
 * the parent `ActivityMap` wrapper, not here.
 */
export function ActivityMap({ activityId, activity, layout = 'fixed' }: ActivityMapProps) {
  const theme = useActivityStore((s) => s.ui.theme)
  const mapLayers = useActivityStore((s) => s.ui.mapLayers)
  const hoveredTimestampMs = useActivityStore((s) => s.hoveredTimestampMs)
  const selectedRangeMs = useActivityStore((s) => s.selectedRangeMs)
  const setHoveredTimestampMs = useActivityStore((s) => s.setHoveredTimestampMs)
  const mapPickMode = useActivityStore((s) => s.mapPickMode)
  const setMapPickMode = useActivityStore((s) => s.setMapPickMode)
  const setPickedMapPoint = useActivityStore((s) => s.setPickedMapPoint)
  const applyOperationToSelected = useActivityStore((s) => s.applyOperationToSelected)
  const mapEditMode = useActivityStore((s) => s.mapEditMode)
  const setMapEditMode = useActivityStore((s) => s.setMapEditMode)
  const maximized = useActivityStore((s) => s.ui.mapMaximized === true)
  const setMapMaximized = useActivityStore((s) => s.setMapMaximized)

  const gpsPoints = useMemo(() => extractGpsPoints(activity.records), [activity.records])
  const outliers = useMemo(() => detectGpsOutliers(activity.records), [activity.records])

  const onRepairOutlier = useCallback(
    (timestamp: Date) => {
      void applyOperationToSelected({ type: 'repairOutlier', timestamp })
    },
    [applyOperationToSelected],
  )

  if (gpsPoints.length === 0) {
    return (
      <section
        aria-label="Map"
        className={cn(
          'border-border bg-card text-muted-foreground flex flex-col items-center justify-center gap-1 rounded-lg border p-4 text-center text-sm',
          layout === 'fixed' ? 'h-64 md:h-80' : SECTION_LAYOUT_CLASSNAME[layout],
        )}
      >
        <p className="text-foreground font-medium">No GPS data in this activity.</p>
        <p>The rest of the editor is unaffected -- use the timeline below to review its metrics.</p>
      </section>
    )
  }

  return (
    <MapCanvas
      activityId={activityId}
      gpsPoints={gpsPoints}
      outliers={outliers}
      theme={theme}
      mapLayers={mapLayers ?? DEFAULT_MAP_LAYER_PREFERENCES}
      hoveredTimestampMs={hoveredTimestampMs}
      selectedRangeMs={selectedRangeMs}
      onHover={setHoveredTimestampMs}
      pickMode={mapPickMode}
      onPickPoint={(lat, lon) => setPickedMapPoint({ lat, lon })}
      onCancelPick={() => setMapPickMode(false)}
      onRepairOutlier={onRepairOutlier}
      activity={activity}
      editMode={mapEditMode}
      onEditModeChange={setMapEditMode}
      layout={layout}
      maximized={maximized}
      onMaximizedChange={setMapMaximized}
    />
  )
}

interface MapCanvasProps {
  activityId: string
  gpsPoints: GpsPoint[]
  outliers: ReturnType<typeof detectGpsOutliers>
  theme: Theme
  mapLayers: MapLayerPreferences
  hoveredTimestampMs: number | null
  selectedRangeMs: { start: number; end: number } | null
  onHover: (timestampMs: number | null) => void
  /** "Pick a point on the map" mode for `setGpsConstant` -- see the store's `mapPickMode` doc comment. */
  pickMode: boolean
  onPickPoint: (lat: number, lon: number) => void
  onCancelPick: () => void
  onRepairOutlier: (timestamp: Date) => void
  activity: Activity
  /** Point-editing mode -- see the store's `mapEditMode`. */
  editMode: boolean
  onEditModeChange: (enabled: boolean) => void
  layout: ActivityMapLayout
  /** The "Maximise map" toggle state (persisted UI pref); the parent turns it into `layout`. */
  maximized: boolean
  onMaximizedChange: (maximized: boolean) => void
}

function MapCanvas({
  activityId,
  gpsPoints,
  outliers,
  theme,
  mapLayers,
  hoveredTimestampMs,
  selectedRangeMs,
  onHover,
  pickMode,
  onPickPoint,
  onCancelPick,
  onRepairOutlier,
  activity,
  editMode,
  onEditModeChange,
  layout,
  maximized,
  onMaximizedChange,
}: MapCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MapLibreMap | null>(null)
  const readyRef = useRef(false)
  const startMarkerRef = useRef<InstanceType<typeof maplibregl.Marker> | null>(null)
  const finishMarkerRef = useRef<InstanceType<typeof maplibregl.Marker> | null>(null)
  /** The map once loaded (state, not just a ref, so the point-editing hook re-binds to it). */
  const [liveMap, setLiveMap] = useState<MapLibreMap | null>(null)
  /** Bumped on every `style.load` after the first, so the point-editing layer is re-installed. */
  const [styleVersion, setStyleVersion] = useState(0)
  const editModeRef = useRef(editMode)
  useEffect(() => {
    editModeRef.current = editMode
  }, [editMode])
  // Read by the long-lived click handlers bound once per activity/theme
  // (see the big effect below) -- a plain prop would be stale there,
  // since that effect doesn't re-run when `pickMode` toggles.
  const pickModeRef = useRef(pickMode)
  const onPickPointRef = useRef(onPickPoint)
  const onRepairOutlierRef = useRef(onRepairOutlier)
  // Display units for the GPS-jump popup's speeds (same stale-closure reason).
  const units = useUnits()
  const unitsRef = useRef(units)
  useEffect(() => {
    unitsRef.current = units
  }, [units])
  const cancelPickButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    pickModeRef.current = pickMode
    // `setGpsConstant`'s "pick on map" flow closes its own dialog (so the
    // map underneath is actually clickable) before entering pick mode --
    // with no `DialogTrigger` to return focus to, that would otherwise
    // drop keyboard focus to `<body>` with no indication of where to go
    // next. Moving focus to this banner's own Cancel button instead keeps
    // a keyboard user oriented and gives them an immediate, reachable way
    // out, regardless of which dialog (currently only one) put the map
    // into pick mode.
    if (pickMode) cancelPickButtonRef.current?.focus()
  }, [pickMode])
  useEffect(() => {
    onPickPointRef.current = onPickPoint
  }, [onPickPoint])
  useEffect(() => {
    onRepairOutlierRef.current = onRepairOutlier
  }, [onRepairOutlier])

  const throttledHover = useThrottled(onHover, 50)

  // --- Basemap / overlays / 3D ---------------------------------------------
  // Keys are build-time constants; read once per mount.
  const keys = useMemo(() => getMapApiKeys(), [])
  const resolvedLayers = useMemo(() => resolveMapLayers(mapLayers, keys), [mapLayers, keys])
  const styleKey = baseStyleKey(resolvedLayers.basemap, theme)
  const extrasPlan = useMemo(
    () => buildMapExtras({ ...resolvedLayers, theme, keys }),
    [resolvedLayers, theme, keys],
  )
  const terrain3d = resolvedLayers.terrain3d
  // Latest layer choice, read by the long-lived map handlers (initial load,
  // post-`setStyle` reload) that are bound once per map instance.
  const layersRef = useRef({ resolvedLayers, extrasPlan })
  useEffect(() => {
    layersRef.current = { resolvedLayers, extrasPlan }
  }, [resolvedLayers, extrasPlan])
  /** Extras currently installed in the live style (reset to [] whenever the style is replaced). */
  const installedExtrasRef = useRef<MapExtra[]>([])
  /** `baseStyleKey` of the style currently applied to the live map. */
  const appliedStyleKeyRef = useRef<string | null>(null)
  /** The map's attribution control, replaced when the basemap's own extra attribution changes. */
  const attributionRef = useRef<AttributionControlRef['current']>(null)
  // Latest hover/selection, so the overlay sources can be refilled after a
  // style reload (which recreates them empty).
  const syncStateRef = useRef({ hoveredTimestampMs, selectedRangeMs, gpsPoints })
  useEffect(() => {
    syncStateRef.current = { hoveredTimestampMs, selectedRangeMs, gpsPoints }
  }, [hoveredTimestampMs, selectedRangeMs, gpsPoints])

  const outlierRecordIndexes = useMemo(() => new Set(outliers.map((o) => o.index)), [outliers])
  const outlierPoints = useMemo(
    () => gpsPoints.filter((p) => outlierRecordIndexes.has(p.recordIndex)),
    [gpsPoints, outlierRecordIndexes],
  )
  const start = gpsPoints[0]
  const finish = gpsPoints[gpsPoints.length - 1]
  // Latest route data, read by `installActivityLayers` (which also runs on
  // later style reloads) and the GPS-jump click handler, so neither uses
  // the data from when the map was created once the activity was edited
  // in place.
  const dataRef = useRef({ gpsPoints, outlierPoints, outliers })
  useEffect(() => {
    dataRef.current = { gpsPoints, outlierPoints, outliers }
  }, [gpsPoints, outlierPoints, outliers])

  // Create (and fully recreate on activity/theme change) the map instance,
  // its sources/layers, and the start/finish markers. Recreating on theme
  // change is a deliberate simplification over a partial `setStyle`.
  // Basemap switches *do* use `setStyle` (so the camera/3D view survives a
  // basemap change); the app's own layers are re-installed on the
  // resulting `style.load` by `installActivityLayers`, and the DOM markers
  // and layer-id-bound handlers survive a style swap untouched.
  useEffect(() => {
    const container = containerRef.current
    if (container == null) return

    const colors = getThemeColors(theme)
    const initialBounds = computeLngLatBounds(gpsPoints)
    const initialLayers = layersRef.current.resolvedLayers
    appliedStyleKeyRef.current = baseStyleKey(initialLayers.basemap, theme)
    installedExtrasRef.current = []
    const initialStyle = buildBaseStyle(initialLayers.basemap, theme, keys)
    const rememberedCamera = getRememberedCamera(activityId)
    const map = new maplibregl.Map({
      container,
      // Added below instead, so a basemap's own extra attribution (e.g.
      // MapTiler's) can be swapped in on a basemap switch.
      attributionControl: false,
      // A style that needs `transformStyle` (e.g. Liberty Topo) has to go
      // through `setStyle` -- the constructor has no such option.
      ...(initialStyle.transformStyle == null ? { style: initialStyle.style } : {}),
      fitBoundsOptions: { padding: 40 },
      ...(rememberedCamera != null
        ? {
            center: rememberedCamera.center,
            zoom: rememberedCamera.zoom,
            bearing: rememberedCamera.bearing,
            pitch: initialLayers.terrain3d ? rememberedCamera.pitch : 0,
          }
        : {
            pitch: initialLayers.terrain3d ? TERRAIN_PITCH : 0,
            ...(initialBounds != null ? { bounds: initialBounds } : {}),
          }),
    })
    // Compass shown (with pitch visualization) since the map can now be
    // rotated/tilted (right-drag / Ctrl+drag, MapLibre's defaults) -- it
    // doubles as the "reset bearing/pitch" control.
    map.addControl(
      new maplibregl.NavigationControl({ showCompass: true, visualizePitch: true }),
      'top-right',
    )
    attributionRef.current = null
    syncAttributionControl(map, attributionRef, initialLayers.basemap)
    if (initialStyle.transformStyle != null) {
      map.setStyle(initialStyle.style, { transformStyle: initialStyle.transformStyle })
    }
    mapRef.current = map
    readyRef.current = false

    const popup = new maplibregl.Popup({ closeButton: true, closeOnClick: true })

    /** Brings basemap extras (terrain, hillshade, overlays, buildings) in line with the latest choice. */
    const syncExtras = () => {
      const { extras, terrain } = layersRef.current.extrasPlan
      reconcileMapExtras(map, installedExtrasRef.current, extras, terrain, 'route-line')
      installedExtrasRef.current = extras
    }

    /** Re-fills the hover/selected sources from the latest store state. */
    const refreshSyncSources = () => {
      const state = syncStateRef.current
      updateSyncSources(map, state.gpsPoints, state.hoveredTimestampMs, state.selectedRangeMs)
    }

    const installActivityLayers = () => {
      const data = dataRef.current
      map.addSource(SOURCES.route, { type: 'geojson', data: pointsToLineString(data.gpsPoints) })
      map.addLayer({
        id: 'route-line',
        type: 'line',
        source: SOURCES.route,
        paint: { 'line-color': colors.primary, 'line-width': 3, 'line-opacity': 0.9 },
      })

      map.addSource(SOURCES.selected, { type: 'geojson', data: emptyFeatureCollection() })
      map.addLayer({
        id: 'selected-segment-line',
        type: 'line',
        source: SOURCES.selected,
        paint: { 'line-color': '#f59e0b', 'line-width': 5, 'line-opacity': 0.95 },
      })

      map.addSource(SOURCES.points, {
        type: 'geojson',
        data: pointsToFeatureCollection(data.gpsPoints),
      })
      map.addLayer({
        id: 'points-hit',
        type: 'circle',
        source: SOURCES.points,
        paint: {
          'circle-radius': 3,
          'circle-color': colors.primary,
          'circle-opacity': 0.35,
        },
      })

      map.addSource(SOURCES.outliers, {
        type: 'geojson',
        data: pointsToFeatureCollection(data.outlierPoints),
      })
      map.addLayer({
        id: 'outlier-points',
        type: 'circle',
        source: SOURCES.outliers,
        // Hidden in point-editing mode (see the edit-mode effect below).
        layout: { visibility: editModeRef.current ? 'none' : 'visible' },
        paint: {
          'circle-radius': 7,
          'circle-color': '#f59e0b',
          'circle-stroke-color': '#7c2d12',
          'circle-stroke-width': 2,
        },
      })

      map.addSource(SOURCES.hover, { type: 'geojson', data: emptyFeatureCollection() })
      map.addLayer({
        id: 'hover-point',
        type: 'circle',
        source: SOURCES.hover,
        paint: {
          'circle-radius': 9,
          'circle-color': colors.primary,
          'circle-stroke-color': theme === 'dark' ? '#0a0a0a' : '#ffffff',
          'circle-stroke-width': 2,
        },
      })
    }

    const onLoad = () => {
      syncExtras()
      installActivityLayers()

      if (start != null) {
        const startMarker = new maplibregl.Marker({
          element: createMarkerElement('start', theme),
          anchor: 'center',
        })
          .setLngLat([start.lon, start.lat])
          .addTo(map)
        startMarkerRef.current = startMarker
      }
      if (finish != null && finish !== start) {
        const finishMarker = new maplibregl.Marker({
          element: createMarkerElement('finish', theme),
          anchor: 'center',
        })
          .setLngLat([finish.lon, finish.lat])
          .addTo(map)
        finishMarkerRef.current = finishMarker
      }

      if (rememberedCamera == null) {
        const bounds = computeLngLatBounds(gpsPoints)
        if (bounds != null) map.fitBounds(bounds, { padding: 40, duration: 0 })
      }

      readyRef.current = true
      setLiveMap(map)

      map.on('moveend', () => {
        const center = map.getCenter()
        rememberCamera(activityId, {
          center: [center.lng, center.lat],
          zoom: map.getZoom(),
          bearing: map.getBearing(),
          pitch: map.getPitch(),
        })
      })

      // Fires after every later `setStyle` (basemap switch): the new style
      // has none of the app's sources/layers, so re-install them on top of
      // the new basemap, then restore the hover/selection highlight.
      map.on('style.load', () => {
        installedExtrasRef.current = []
        syncExtras()
        installActivityLayers()
        refreshSyncSources()
        readyRef.current = true
        setStyleVersion((v) => v + 1)
      })

      const setHoverFromFeature = (feature: MapGeoJSONFeature | undefined) => {
        const timestampMs = feature?.properties?.timestampMs
        if (typeof timestampMs === 'number') throttledHover(timestampMs)
      }

      map.on('mousemove', 'points-hit', (e: MapLayerMouseEvent) => {
        if (!pickModeRef.current && !editModeRef.current) map.getCanvas().style.cursor = 'pointer'
        setHoverFromFeature(e.features?.[0])
      })
      map.on('mouseleave', 'points-hit', () => {
        if (!pickModeRef.current && !editModeRef.current) map.getCanvas().style.cursor = ''
      })

      map.on('mouseenter', 'outlier-points', () => {
        if (!pickModeRef.current && !editModeRef.current) map.getCanvas().style.cursor = 'pointer'
      })
      map.on('mouseleave', 'outlier-points', () => {
        if (!pickModeRef.current && !editModeRef.current) map.getCanvas().style.cursor = ''
      })
      map.on('click', 'outlier-points', (e: MapLayerMouseEvent) => {
        if (pickModeRef.current) {
          onPickPointRef.current(e.lngLat.lat, e.lngLat.lng)
          return
        }
        // GPS-jump popups are off while point editing (the layer is hidden too).
        if (editModeRef.current) return
        const feature = e.features?.[0]
        const candidate = dataRef.current.outliers.find(
          (o) => o.index === feature?.properties?.recordIndex,
        )
        if (candidate == null) return
        setHoverFromFeature(feature)
        const repairButtonHtml =
          candidate.timestamp != null
            ? `<button type="button" data-action="repair-outlier" style="margin-top:6px;font-size:11px;padding:3px 8px;border-radius:4px;border:1px solid #92400e;background:#f59e0b;color:#1a1a1a;cursor:pointer;">Repair this point</button>`
            : ''
        popup
          .setLngLat(e.lngLat)
          .setHTML(
            // MapLibre's default popup chrome is always light (white
            // background), regardless of the app's theme -- without an
            // explicit color here, this content inherits the app shell's
            // *dark-mode* text color (near-white, meant for a dark
            // background) via normal CSS cascade, which is nearly
            // invisible on the popup's white background. Confirmed live:
            // this was unreadable before the explicit color was added.
            `<div style="font-size:12px;max-width:220px;color:#1a1a1a">` +
              `<strong>Possible GPS jump (suggestion only)</strong><br/>` +
              (candidate.timestamp != null
                ? `${formatTimestamp(candidate.timestamp, 'local')}<br/>`
                : '') +
              `In: ${formatSpeedMetersPerSecond(candidate.incomingSpeedMetersPerSecond, unitsRef.current)} &middot; ` +
              `Out: ${formatSpeedMetersPerSecond(candidate.outgoingSpeedMetersPerSecond, unitsRef.current)}<br/>` +
              `<em>Not an applied edit unless you repair it below.</em>` +
              repairButtonHtml +
              `</div>`,
          )
          .addTo(map)

        // MapLibre popups are a plain HTML string handed to `setHTML` --
        // there's no JSX/event-prop wiring available, so the "Repair
        // this point" button's click has to be attached via real DOM
        // event delegation against the popup's actual content element,
        // which exists synchronously once `.addTo(map)` has run.
        const container = popup.getElement()
        const repairButton = container?.querySelector<HTMLButtonElement>(
          '[data-action="repair-outlier"]',
        )
        repairButton?.addEventListener('click', () => {
          if (candidate.timestamp != null) onRepairOutlierRef.current(candidate.timestamp)
          popup.remove()
        })
      })

      map.on('click', (e: MapMouseEvent) => {
        if (pickModeRef.current) {
          onPickPointRef.current(e.lngLat.lat, e.lngLat.lng)
          return
        }
        if (editModeRef.current) return // the point-editing hook owns clicks in edit mode
        const features = map.queryRenderedFeatures(e.point, {
          layers: ['points-hit', 'outlier-points'],
        })
        if (features.length === 0) onHover(null)
      })
    }

    map.on('load', onLoad)

    return () => {
      map.remove()
      mapRef.current = null
      readyRef.current = false
      startMarkerRef.current = null
      finishMarkerRef.current = null
      setLiveMap(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- full recreation is intentional; see comment above
  }, [activityId, theme])

  // Basemap switch: swap the style in place (camera preserved). A theme
  // change recreates the map instead (effect above), which already applies
  // the current basemap -- so this only acts when the key really differs
  // from what's on the live map. `diff: false` forces a full reload so
  // `style.load` always fires and re-installs the app's layers.
  useEffect(() => {
    const map = mapRef.current
    if (map == null || !readyRef.current || appliedStyleKeyRef.current === styleKey) return
    appliedStyleKeyRef.current = styleKey
    readyRef.current = false
    installedExtrasRef.current = []
    syncAttributionControl(map, attributionRef, resolvedLayers.basemap)
    const base = buildBaseStyle(resolvedLayers.basemap, theme, keys)
    map.setStyle(base.style, {
      diff: false,
      ...(base.transformStyle != null ? { transformStyle: base.transformStyle } : {}),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on `styleKey` only; see above
  }, [styleKey])

  // Overlay / 3D changes: reconcile in place, no style reload. While a
  // style reload is in flight (`readyRef` false) this is skipped -- the
  // `style.load` handler applies the latest plan itself.
  useEffect(() => {
    const map = mapRef.current
    if (map == null || !readyRef.current) return
    reconcileMapExtras(
      map,
      installedExtrasRef.current,
      extrasPlan.extras,
      extrasPlan.terrain,
      'route-line',
    )
    installedExtrasRef.current = extrasPlan.extras
  }, [extrasPlan])

  // Tilt into / out of 3D when the user toggles it (not on mount: a new
  // map is constructed with the right pitch already).
  const previousTerrain3dRef = useRef(terrain3d)
  useEffect(() => {
    if (previousTerrain3dRef.current === terrain3d) return
    previousTerrain3dRef.current = terrain3d
    const map = mapRef.current
    if (map == null) return
    map.easeTo(
      terrain3d ? { pitch: TERRAIN_PITCH, duration: 800 } : { pitch: 0, bearing: 0, duration: 800 },
    )
  }, [terrain3d])

  // Lightweight overlay updates (hover marker, selected-segment highlight)
  // that don't require recreating the whole map.
  useEffect(() => {
    const map = mapRef.current
    if (map == null || !readyRef.current) return
    updateSyncSources(map, gpsPoints, hoveredTimestampMs, selectedRangeMs)
  }, [hoveredTimestampMs, selectedRangeMs, gpsPoints])

  // Cursor feedback for pick mode -- a crosshair makes it obvious the next
  // click means something different than usual, independent of whether
  // the banner below has been noticed.
  useEffect(() => {
    const map = mapRef.current
    if (map == null || !readyRef.current) return
    map.getCanvas().style.cursor = pickMode ? 'crosshair' : ''
  }, [pickMode])

  // The activity changed without the map being recreated (an edit, undo
  // or redo): refresh the route, the hover-hit points, the GPS-jump
  // markers and the start/finish markers in place, keeping the camera.
  const renderedPointsRef = useRef(gpsPoints)
  useEffect(() => {
    const map = liveMap
    if (map == null || renderedPointsRef.current === gpsPoints) return
    renderedPointsRef.current = gpsPoints
    ;(map.getSource(SOURCES.route) as GeoJSONSource | undefined)?.setData(
      pointsToLineString(gpsPoints),
    )
    ;(map.getSource(SOURCES.points) as GeoJSONSource | undefined)?.setData(
      pointsToFeatureCollection(gpsPoints),
    )
    ;(map.getSource(SOURCES.outliers) as GeoJSONSource | undefined)?.setData(
      pointsToFeatureCollection(outlierPoints),
    )
    if (start != null) startMarkerRef.current?.setLngLat([start.lon, start.lat])
    if (finish != null) finishMarkerRef.current?.setLngLat([finish.lon, finish.lat])
  }, [liveMap, gpsPoints, outlierPoints, start, finish])

  // Point-editing mode and the GPS-jump markers/popup exclude each other.
  useEffect(() => {
    const map = liveMap
    if (map == null || map.getLayer('outlier-points') == null) return
    map.setLayoutProperty('outlier-points', 'visibility', editMode ? 'none' : 'visible')
  }, [liveMap, editMode, styleVersion])

  const editing = useMapPointEditing({
    map: liveMap,
    styleVersion,
    enabled: editMode && !pickMode,
    activity,
    gpsPoints,
    theme,
    routeSourceId: SOURCES.route,
    onHover,
  })

  // Maximise / collapse only change CSS: tell MapLibre about the new
  // container size right away (its own ResizeObserver would catch up a
  // frame later). `resize()` keeps the camera's center/zoom.
  const previousLayoutRef = useRef(layout)
  useEffect(() => {
    if (previousLayoutRef.current === layout) return
    previousLayoutRef.current = layout
    mapRef.current?.resize()
  }, [layout])

  // Esc anywhere in the map area restores a maximised map -- unless point
  // editing / pick mode is active (Esc belongs to them there) or something
  // else (e.g. a menu closing) already handled it. Events bubbling through
  // React portals (the layer menu) are ignored: only real DOM descendants.
  const onSectionKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (event.key !== 'Escape' || !maximized || editMode || pickMode) return
      if (event.defaultPrevented || !event.currentTarget.contains(event.target as Node)) return
      event.preventDefault()
      onMaximizedChange(false)
    },
    [maximized, editMode, pickMode, onMaximizedChange],
  )

  const zoomToRoute = useCallback(() => {
    const bounds = computeLngLatBounds(gpsPoints)
    if (bounds != null) mapRef.current?.fitBounds(bounds, { padding: 40 })
  }, [gpsPoints])

  const summary = useMemo(() => {
    const startText =
      start?.timestampMs != null ? new Date(start.timestampMs).toLocaleString() : 'unknown time'
    const finishText =
      finish?.timestampMs != null ? new Date(finish.timestampMs).toLocaleString() : 'unknown time'
    return (
      `Map showing a GPS route with ${gpsPoints.length} recorded positions, starting at ${startText} ` +
      `and finishing at ${finishText}` +
      (outliers.length > 0
        ? `, with ${outliers.length} possible GPS jump${outliers.length === 1 ? '' : 's'} flagged for review.`
        : '.')
    )
  }, [gpsPoints.length, start, finish, outliers.length])

  return (
    <section
      aria-label="Map"
      className={cn('relative flex flex-col gap-1', SECTION_LAYOUT_CLASSNAME[layout])}
      onKeyDown={onSectionKeyDown}
    >
      <span className="sr-only">{summary}</span>
      <div className="absolute top-2.5 left-2.5 z-10 flex flex-col gap-1.5">
        <MapLayerControl />
        <button
          type="button"
          aria-label="Edit points"
          aria-pressed={editMode}
          title={
            pickMode
              ? 'Edit points (not available while picking a location)'
              : editMode
                ? 'Stop editing points'
                : 'Edit points: drag, insert and delete GPS points'
          }
          disabled={pickMode}
          onClick={() => onEditModeChange(!editMode)}
          className={cn(
            MAP_BUTTON_CLASSNAME,
            editMode && 'bg-primary text-primary-foreground hover:bg-primary/90',
          )}
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Zoom to route"
          title="Zoom to route"
          onClick={zoomToRoute}
          className={MAP_BUTTON_CLASSNAME}
        >
          <Maximize2 className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Maximise map"
          aria-pressed={maximized}
          title={maximized ? 'Restore map size (Esc)' : 'Maximise map'}
          onClick={() => onMaximizedChange(!maximized)}
          className={cn(
            MAP_BUTTON_CLASSNAME,
            maximized && 'bg-primary text-primary-foreground hover:bg-primary/90',
          )}
        >
          {maximized ? (
            <Minimize className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Fullscreen className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>
      {resolvedLayers.basemap.logo === 'maptiler' && <MapTilerLogo />}
      {editing.selectionBox != null && (
        <div
          aria-hidden="true"
          className="border-primary bg-primary/10 pointer-events-none absolute z-10 border border-dashed"
          style={{
            left: Math.min(editing.selectionBox.x0, editing.selectionBox.x1),
            top: Math.min(editing.selectionBox.y0, editing.selectionBox.y1),
            width: Math.abs(editing.selectionBox.x1 - editing.selectionBox.x0),
            height: Math.abs(editing.selectionBox.y1 - editing.selectionBox.y0),
          }}
        />
      )}
      {editMode && !pickMode && (
        <MapEditToolbar editing={editing} onDone={() => onEditModeChange(false)} />
      )}
      {pickMode && (
        <div
          role="status"
          className="border-primary bg-popover text-popover-foreground absolute inset-x-2 top-2 z-10 flex items-center justify-between gap-2 rounded-md border px-3 py-1.5 text-xs shadow-md"
        >
          <span>Click the map to set this point&apos;s GPS location.</span>
          <button
            ref={cancelPickButtonRef}
            type="button"
            onClick={onCancelPick}
            className="border-border hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring rounded-md border px-2 py-0.5 outline-none focus-visible:ring-2"
          >
            Cancel
          </button>
        </div>
      )}
      {/*
        `maplibregl-map` is the class MapLibre adds to its container itself;
        it's repeated here because React rewrites the whole className when
        `layout` changes, which would drop it -- and with it MapLibre's
        `position: relative`, so the absolutely positioned start/finish
        markers stopped being clipped by this box and showed over the
        dashboard when panned out of the map (and `index.css`'s attribution
        theming, keyed on the class, stopped applying).
      */}
      <div
        ref={containerRef}
        aria-hidden="true"
        className={cn(
          'maplibregl-map border-border relative w-full overflow-hidden rounded-lg border',
          CANVAS_LAYOUT_CLASSNAME[layout],
        )}
      />
    </section>
  )
}

/**
 * Keeps the map's attribution control in line with the basemap's own
 * extra attribution (`MapLayerEntry.attribution`, shown via
 * `customAttribution` on top of whatever the sources carry). The control
 * is only replaced when that text actually changes.
 */
function syncAttributionControl(
  map: MapLibreMap,
  ref: AttributionControlRef,
  basemap: MapLayerEntry,
): void {
  const custom = basemap.attribution
  if (ref.current != null) {
    if (ref.current.custom === custom) return
    map.removeControl(ref.current.control)
  }
  const control = new maplibregl.AttributionControl(
    custom != null ? { customAttribution: custom } : {},
  )
  map.addControl(control, 'bottom-right')
  ref.current = { control, custom }
}

/**
 * MapTiler's free plan requires its logo on the map: a plain text
 * wordmark (no hotlinked image) linking to MapTiler, bottom-left (the
 * attribution control sits bottom-right).
 */
function MapTilerLogo() {
  return (
    <a
      href="https://www.maptiler.com"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="MapTiler (opens in a new tab)"
      className="bg-background/85 text-foreground focus-visible:ring-ring absolute bottom-2 left-2 z-10 rounded-md px-2 py-0.5 text-xs font-bold tracking-tight shadow-sm outline-none focus-visible:ring-2"
    >
      MapTiler
    </a>
  )
}
